import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useAcesso } from 'dashboard/composables/useAcesso';

/**
 * Deploy do PowerFlow em produção (D2, decisão do Murilo de 16/10/2026): quem monta flow na tela.
 *
 * O super-admin sempre. O gestor da conta só com a chave da instalação `POWERFLOW_ADMIN_MONTA_FLOW`
 * ligada, que chega pelo `window.globalConfig` (store `globalConfig`). Quem decide de verdade é o
 * servidor (`PowerAgentsGuard#gestor_monta_flow?`); isto é a porta da frente, para o gestor não abrir
 * uma tela em que nada grava.
 *
 * FLOW-06 (Task 6.2; PLAN §0.1, item 10): o gestor é o do servidor (`gestor_com_flag?`): o
 * administrador e quem recebeu `poweragents_manage` num papel personalizado. Até aqui só o
 * administrador contava, e o `poweragents_manage` com a chave ligada montava pela API, mas não via o
 * "Novo flow" nem o "Abrir no editor". `can` é a faixa do papel e é falsa para o administrador (ver
 * `usePermissions`), por isso soma com o `isAdmin`.
 *
 * - `montaFlow`: abre o editor, o "Abrir no editor", o "Novo flow" e a "Nova ferramenta HTTP".
 *
 * Emenda da FLOW-13 de 18/10 (Murilo): sem a chave, o gestor não vê os botões de criar. Antes, o
 * `soEquipeMonta` os mostrava desativados, com "Em breve", e saiu junto com eles.
 *
 * ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, o gestor é quem tem o `powerflow:write`, o escopo de montar
 * flow (`tool_catalog#create`, `#update`), como no servidor (`gestor_monta_flow?('powerflow:write')`); desligada, o
 * papel, como acima. A chave da instalação continua por cima do escopo, e o super-admin, fora dela.
 */
export function useMontaFlow() {
  const { isSuperAdmin, isAdmin } = useAdmin();
  const { can } = usePermissions();
  const { decide } = useAcesso();
  const globalConfig = useMapGetter('globalConfig/get');

  const chaveLigada = computed(
    () => globalConfig.value?.powerflowAdminMontaFlow === true
  );
  const montaFlow = computed(
    () =>
      isSuperAdmin.value ||
      (chaveLigada.value &&
        decide(
          'powerflow:write',
          () => isAdmin.value || can('poweragents_manage')
        ))
  );

  return { montaFlow };
}
